/**
 * ConfigStoreAdminPage — edit flowgenix_scenario_os.config_store docs.
 * Catalog-driven list + JSON editor. Platform admins see all keys;
 * domain admins see domain-scoped keys (API enforces write RBAC).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CoachLayout from './CoachLayout';
import { useAuth } from '../../context/AuthContext';

function authHeaders() {
  const token = localStorage.getItem('scenario_os_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const TEMPLATES = {
  'authoring:domain:{domain_id}:content_types': (domainId) => ({
    _id: `authoring:domain:${domainId}:content_types`,
    config_name: 'domain_content_types',
    domain_id: domainId,
    config: {
      allowed_scene_types: [
        'video', 'slide_deck', 'interactive_quiz', 'content_panel',
        'reflection', 'code_lab', 'capstone', 'ai_lab',
      ],
    },
  }),
  scenario_os_config: () => ({
    _id: 'scenario_os_config',
    config_name: 'scenario_os_config',
    config: {},
  }),
  coach_analytics_dashboard_config: () => ({
    _id: 'coach_analytics_dashboard_config',
    config_name: 'coach_analytics_dashboard_config',
    config: { inactive_days: 7, min_sessions: 1 },
  }),
};

export default function ConfigStoreAdminPage() {
  const { isAdmin, isPlatformAdmin, authoringDomains } = useAuth();
  const [configs, setConfigs] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [editor, setEditor] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [scope, setScope] = useState('');
  const [domainFilter, setDomainFilter] = useState('');

  const loadCatalog = useCallback(() => {
    fetch('/scenario-os/admin/config-store/catalog', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setCatalog(data?.catalog || []))
      .catch(() => setCatalog([]));
  }, []);

  const loadList = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: '200' });
    if (q) params.set('q', q);
    if (scope) params.set('scope', scope);
    if (domainFilter) params.set('domain_id', domainFilter);
    fetch(`/scenario-os/admin/config-store?${params}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : r.json().then((d) => Promise.reject(d))))
      .then((data) => setConfigs(data.configs || []))
      .catch((e) => setError(e.detail || e.message || 'Failed to load configs'))
      .finally(() => setLoading(false));
  }, [q, scope, domainFilter]);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);
  useEffect(() => { loadList(); }, [loadList]);

  const catalogLabels = useMemo(() => {
    const m = {};
    catalog.forEach((c) => { m[c.key] = c.label; });
    return m;
  }, [catalog]);

  const openDoc = async (id) => {
    setError('');
    setSuccess('');
    setSelectedId(id);
    try {
      const r = await fetch(`/scenario-os/admin/config-store/${encodeURIComponent(id)}`, {
        headers: authHeaders(),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Load failed');
      setEditor(JSON.stringify(data, null, 2));
    } catch (e) {
      setError(e.message);
    }
  };

  const createFromCatalog = async (entry) => {
    setError('');
    setSuccess('');
    let doc;
    if (entry.key.includes('{domain_id}')) {
      const domainId = domainFilter || authoringDomains?.[0] || 'ai';
      const factory = TEMPLATES[entry.key];
      doc = factory ? factory(domainId) : {
        _id: entry.key.replace('{domain_id}', domainId),
        config_name: entry.key,
        config: {},
      };
    } else if (TEMPLATES[entry.key]) {
      doc = TEMPLATES[entry.key]();
    } else {
      doc = {
        _id: entry.key.startsWith('role_definition') ? 'role_definition:custom' : entry.key,
        config_name: entry.key,
        config: {},
      };
    }
    // If exists, open it
    const existing = configs.find((c) => c._id === doc._id || c.config_name === entry.key);
    if (existing) {
      openDoc(existing._id);
      return;
    }
    setSelectedId(doc._id);
    setEditor(JSON.stringify(doc, null, 2));
    setSuccess(`New stub for ${entry.label}. Edit JSON and Save to create.`);
  };

  const save = async () => {
    setError('');
    setSuccess('');
    try {
      const parsed = JSON.parse(editor);
      const docId = parsed._id || selectedId;
      const { _id, ...rest } = parsed;
      const r = await fetch('/scenario-os/admin/config-store', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          doc_id: docId,
          config_name: rest.config_name,
          data: rest,
          merge: false,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Save failed');
      setSuccess('Saved.');
      setSelectedId(docId);
      loadList();
    } catch (e) {
      setError(e.message || 'Invalid JSON or save failed');
    }
  };

  const remove = async () => {
    if (!isPlatformAdmin || !selectedId) return;
    if (!window.confirm(`Delete ${selectedId}?`)) return;
    setError('');
    try {
      const r = await fetch(`/scenario-os/admin/config-store/${encodeURIComponent(selectedId)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Delete failed');
      setSuccess('Deleted.');
      setSelectedId('');
      setEditor('');
      loadList();
    } catch (e) {
      setError(e.message);
    }
  };

  if (!isAdmin) {
    return (
      <CoachLayout title="Config Store">
        <div style={{ padding: 24 }}>Admin access required.</div>
      </CoachLayout>
    );
  }

  return (
    <CoachLayout title="Config Store">
      <div style={{ display: 'grid', gridTemplateColumns: '280px 300px 1fr', gap: 16, padding: 16, minHeight: 480 }}>
        {/* Catalog */}
        <div>
          <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Catalog</div>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
            Known TeachIQ product configs. Click to open or stub-create.
          </div>
          <div style={{ maxHeight: 560, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
            {catalog.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => createFromCatalog(c)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px',
                  border: 'none', borderBottom: '1px solid #f1f5f9', background: '#fff',
                  cursor: 'pointer', fontSize: 12,
                }}
              >
                <div style={{ fontWeight: 600 }}>{c.label}</div>
                <div style={{ color: '#64748b', fontFamily: 'monospace', fontSize: 10 }}>{c.key}</div>
                <div style={{ color: '#94a3b8', fontSize: 10 }}>{c.scope}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Existing docs */}
        <div>
          <div style={{ marginBottom: 8, fontSize: 13, color: '#64748b' }}>
            flowgenix_scenario_os only
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              style={{ flex: 1, padding: 6, borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
            >
              <option value="">All scopes</option>
              <option value="platform">Platform</option>
              <option value="domain">Domain</option>
            </select>
            <select
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value)}
              style={{ flex: 1, padding: 6, borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12 }}
            >
              <option value="">Any domain</option>
              {(authoringDomains || ['ai']).map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          <input
            placeholder="Filter…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ width: '100%', marginBottom: 8, padding: 8, borderRadius: 6, border: '1px solid #cbd5e1' }}
          />
          {loading && <div style={{ color: '#94a3b8' }}>Loading…</div>}
          <div style={{ maxHeight: 520, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
            {configs.map((c) => (
              <button
                key={c._id}
                type="button"
                onClick={() => openDoc(c._id)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px',
                  border: 'none', borderBottom: '1px solid #f1f5f9',
                  background: selectedId === c._id ? '#fee2e2' : '#fff',
                  cursor: 'pointer', fontSize: 12,
                }}
              >
                <div style={{ fontWeight: 600, color: '#0f172a' }}>{c._id}</div>
                <div style={{ color: '#64748b' }}>
                  {catalogLabels[c.config_name] || c.config_name || c.domain_id || ''}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Editor */}
        <div>
          {error && <div style={{ color: '#dc2626', marginBottom: 8 }} data-testid="config-store-error">{error}</div>}
          {success && <div style={{ color: '#16a34a', marginBottom: 8 }} data-testid="config-store-success">{success}</div>}
          {!selectedId && !editor ? (
            <div style={{ color: '#64748b', padding: 24 }}>Select a config document or catalog entry.</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                <button type="button" onClick={save} className="btn btn-primary" data-testid="config-store-save">
                  Save
                </button>
                {isPlatformAdmin && selectedId && (
                  <button type="button" onClick={remove} className="btn btn-outline" data-testid="config-store-delete">
                    Delete
                  </button>
                )}
                <span style={{ fontSize: 12, color: '#64748b', alignSelf: 'center' }}>
                  {isPlatformAdmin ? 'platform_admin — full write' : 'domain admin — domain-scoped write'}
                </span>
              </div>
              <textarea
                value={editor}
                onChange={(e) => setEditor(e.target.value)}
                spellCheck={false}
                data-testid="config-store-editor"
                style={{
                  width: '100%', minHeight: 520, fontFamily: 'ui-monospace, monospace',
                  fontSize: 12, padding: 12, borderRadius: 8, border: '1px solid #cbd5e1',
                }}
              />
            </>
          )}
        </div>
      </div>
    </CoachLayout>
  );
}
